// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PurchaseOrder } from '@/purchase-order/api/types';
import {
  PHASE_CONFIG,
  isCancelled,
  isFullyReceived,
  parseStatus,
} from '@/purchase-order/constants/status';
import { usePurchaseOrderSearch } from '@/purchase-order/hooks/use-purchase-order-search';
import { Autocomplete, Box, Chip, Stack, TextField } from '@mui/material';
import { ForwardedRef, forwardRef, useEffect, useMemo } from 'react';

interface Props {
  /**
   * The name attribute for the form input element.
   * Used for form integration and accessibility.
   */
  name?: string;

  /**
   * The label text displayed above the input element.
   * Should clearly indicate the purpose of the search field.
   */
  label?: string;

  /**
   * If true, displays the component in an error state with red highlighting.
   * Used for form validation feedback.
   */
  error?: boolean;

  /**
   * Text displayed below the input element.
   * Used for additional context or error messages.
   */
  helperText?: string;

  /**
   * The currently selected purchase order in the search field.
   * Null indicates no selection.
   */
  value: PurchaseOrder | null;

  /**
   * If true, the input field is disabled and cannot be interacted with.
   */
  disabled?: boolean;

  /**
   * Callback fired when a purchase order is selected or deselected.
   * Receives the selected purchase order or null if selection is cleared.
   */
  onChange: (value: PurchaseOrder | null) => void;
}

/**
 * A component that renders a searchable purchase order selector with autocomplete.
 *
 * Features:
 * - Real-time search with fuzzy matching
 * - Shows purchase orders that can still take a shipment, grouped by status
 * - Form integration with error states
 * - Accessibility support via ARIA attributes
 *
 * Search behavior:
 * - Hides cancelled and fully received purchase orders
 * - Groups by status (Draft, Pending, Shipping, etc.)
 * - Searches by PO number
 *
 * @param props - Component properties
 * @param ref - Forwarded ref to the input element
 */
function PurchaseOrderSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { purchaseOrders, isLoading } = usePurchaseOrderSearch({
    filter: {},
  });

  // Extract props for the filter and the resolve effect
  const { value, onChange } = props;

  // A cancelled or fully received order takes no further shipments. The order
  // this shipment already names stays listed so the field can still show it.
  const filteredAndSortedPOs = useMemo(() => {
    return purchaseOrders
      .filter(
        (po) =>
          po.id === value?.id ||
          (!isCancelled(po.status) && !isFullyReceived(po.status))
      )
      .sort(
        (a, b) =>
          PHASE_CONFIG[parseStatus(a.status).phase].displayOrder -
          PHASE_CONFIG[parseStatus(b.status).phase].displayOrder
      );
  }, [purchaseOrders, value]);

  // Auto-resolve stub values to full records after data loads
  // A stub is a value with id/key set but purchaseOrderNumber empty (indicating incomplete data)
  useEffect(() => {
    // Skip if no value or value is already complete (has purchaseOrderNumber)
    if (!value || value.purchaseOrderNumber) {
      return;
    }

    // Value is a stub - find the full record by id or key
    const match = filteredAndSortedPOs.find(
      (po) => po.id === value.id || po.key === value.key
    );

    if (match) {
      onChange(match);
    }
  }, [filteredAndSortedPOs, value, onChange]);

  return (
    <Autocomplete
      ref={ref}
      size="small"
      value={props.value}
      options={filteredAndSortedPOs}
      loading={isLoading}
      loadingText="Loading..."
      disabled={props.disabled}
      getOptionLabel={(option) =>
        option.externalPurchaseOrderNumber
          ? `${option.purchaseOrderNumber} (${option.externalPurchaseOrderNumber})`
          : option.purchaseOrderNumber
      }
      groupBy={(option) => PHASE_CONFIG[parseStatus(option.status).phase].label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(optionProps, option) => (
        <Box component="li" {...optionProps} key={option.id}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <span>
              {option.externalPurchaseOrderNumber
                ? `${option.purchaseOrderNumber} (${option.externalPurchaseOrderNumber})`
                : option.purchaseOrderNumber}
            </span>
            {option.thirdParty.isEnabled && <Chip label="3PL" size="small" />}
          </Stack>
        </Box>
      )}
      onChange={(_, value) => props.onChange(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          label={props.label}
          error={props.error}
          helperText={isLoading ? 'Loading...' : props.helperText}
        />
      )}
    />
  );
}

/**
 * A controlled component for searching and selecting purchase orders.
 * Wraps the PurchaseOrderSearchRenderer with ref forwarding for form integration.
 * See the renderer documentation for full feature details.
 */
export const PurchaseOrderSearch = forwardRef(PurchaseOrderSearchRenderer);
